import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {
  useDataChannel,
  useChat,
  useRemoteParticipants,
  useLocalParticipant,
  useTracks,
} from '@livekit/components-react';
import {RoomEvent, Track} from 'livekit-client';
import {TranscriptionTile} from './TranscriptionTile';
import {useDispatch, useSelector} from 'react-redux';
import {stopOneOnOneMeeting} from 'store/communication/actions/speechToTextServiceActions';
import {openPlans, requestTutorSwitch, setBuyMore, showQuotaPaywall} from 'store/subscriptionUi';
import {SIMPLIFIED_FIRST_VISIT} from 'config/app.config';
import {lowMinutesAction} from 'utils/billing/billingModel';
import {
  listensToLearner,
  METER_TOPIC,
  minutesLeftLabel,
  parseMeterMessage,
  freeTourLabel,
  sessionEndNotice,
  showsMicGuidance,
  TUTOR_SUGGESTIONS,
  TEXT_MODE,
  tutorModeLabel,
  writesReplies,
} from './tutorSessionMeter';
import LiveKitTextInput from './LiveKitTextInput';
import LiveKitTranscriptPanel from './LiveKitTranscriptPanel';
import TutorIdleCard from './TutorIdleCard';
import {
  latestAgentChatMessage,
  TUTOR_CLIENT_READY_MESSAGE,
  TUTOR_OPENING_RECEIVED_PREFIX,
  TUTOR_STILL_HERE_MESSAGE,
} from './tutorChatMessages';
import { useTutorAnalytics } from 'firstVisit/tutorAnalytics';

// Sol tutor-idle C: "Still there?" after 60 s of quiet; tutor mode closes 15 s later without an answer.
const TUTOR_IDLE_WARN_MS = 60000;
const TUTOR_IDLE_CLOSE_MS = 15000;
import { emit as emitFirstVisit, tourSuggestions } from 'firstVisit/events';

const MIC_PERMISSION_DENIED_PATTERNS = [
  'notallowederror',
  'permissiondenied',
  'denied permission',
  'permission denied',
  'microphone permission',
  'securityerror',
];

const isMicrophonePermissionDeniedError = error => {
  const details = [
    error?.name,
    error?.message,
    error?.cause?.message,
    String(error || ''),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return MIC_PERMISSION_DENIED_PATTERNS.some(pattern => details.includes(pattern));
};

export default function LiveKitSDK({micPermissionDenied = false, freeTourQuestion = false, planSecondsLeft = null}) {
  const tracks = useTracks();
  const participants = useRemoteParticipants({
    updateOnlyOn: [RoomEvent.ParticipantMetadataChanged],
  });
  const agentParticipant = participants.find(p => p.isAgent);
  const {
    localParticipant,
    isMicrophoneEnabled,
    microphoneTrack,
    lastMicrophoneError,
  } = useLocalParticipant();
  const {chatMessages, send: sendChatMessage} = useChat();

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );
  const tutorMode = useSelector(
    state => state.communication.speechToTextService.tutorMode || 'VOICE',
  );

  // Guardrail: ensure mic is enabled in 1:1 tutor mode.
  // If the mic gets disabled (permissions reset, device switch, etc) the agent won't hear the user,
  // which matches the "tutor asks first question then never responds" symptom.
  useEffect(() => {
    if (!oneToOneMeetingStatus || !listensToLearner(tutorMode)) return;
    if (!localParticipant) return;
    if (isMicrophoneEnabled) return;
    Promise.resolve(localParticipant.setMicrophoneEnabled(true)).catch(e => {
      console.warn('[LiveKit] Failed to enable microphone', e);
    });
  }, [oneToOneMeetingStatus, tutorMode, localParticipant, isMicrophoneEnabled]);

  useEffect(() => {
    if (!oneToOneMeetingStatus || !listensToLearner(tutorMode)) return;
    if (lastMicrophoneError) {
      console.warn('[LiveKit] lastMicrophoneError', lastMicrophoneError);
    }
  }, [oneToOneMeetingStatus, tutorMode, lastMicrophoneError]);

  const micPermissionDeniedFromLiveKit = useMemo(
    () => isMicrophonePermissionDeniedError(lastMicrophoneError),
    [lastMicrophoneError],
  );
  // Voice only: Chat never shows microphone guidance (Sol ui-leftovers G9).
  const showMicPermissionWarning = showsMicGuidance({
    active: oneToOneMeetingStatus,
    mode: tutorMode,
    micEnabled: isMicrophoneEnabled,
    denied: micPermissionDenied || micPermissionDeniedFromLiveKit,
  });

  useEffect(() => {
    if (!oneToOneMeetingStatus) return;
    if (!microphoneTrack) {
      console.warn('[LiveKit] microphoneTrack publication is missing (not published yet?)');
      return;
    }
    console.log('[LiveKit] microphoneTrack', {
      sid: microphoneTrack.trackSid,
      muted: microphoneTrack.isMuted,
      kind: microphoneTrack.kind,
      source: microphoneTrack.source,
      hasTrack: Boolean(microphoneTrack.track),
    });
  }, [oneToOneMeetingStatus, microphoneTrack]);

  // How long the tutor-transcript stream must stay quiet before we hand the turn
  // back to you ('prompt'). We drive 'speaking' from the tutor's transcript
  // activity, NOT LiveKit VAD: on web the agent's VAD (isSpeaking) misses long
  // stretches of tutor audio — observed ~18s of reported "silence" mid-answer —
  // which flipped the UI to 'prompt' while the tutor was still talking. The
  // transcript stream (livekit_final_response … tutor) is continuous and
  // reliable, so this debounce only has to bridge short inter-sentence gaps.
  const AGENT_RELEASE_MS = 1500;

  // Single source of truth for the turn state:
  //   'connecting' – tutor hasn't joined the room yet
  //   'speaking'   – tutor is talking (you can't type/speak)
  //   'thinking'   – you submitted a question (typed send OR a final spoken
  //                  transcript); the tutor is processing (you can't type/speak)
  //   'prompt'     – your turn: type or speak
  // 'thinking' is entered ONLY on a real submission (see onTranscript and
  // onMessageSent), never from raw VAD — so noise can't flip it. Connection and
  // the tutor's own speech drive the rest.
  const [turn, setTurn] = useState('connecting');
  const releaseTimeout = useRef();

  // React-side transcript (Unity rendering disabled)
  const [transcriptSourceKey, setTranscriptSourceKey] = useState('');
  const [transcriptSourceText, setTranscriptSourceText] = useState('');
  const renderedTutorChatRef = useRef('');
  const clientReadySentRef = useRef(false);
  const openingAckSentRef = useRef(false);

  // Reset turn state whenever tutor mode (re)connects so a stale label doesn't
  // carry over.
  useEffect(() => {
    clearTimeout(releaseTimeout.current);
    renderedTutorChatRef.current = '';
    clientReadySentRef.current = false;
    openingAckSentRef.current = false;
    setTurn('connecting');
    setTranscriptSourceKey('');
    setTranscriptSourceText('');
  }, [oneToOneMeetingStatus]);

  // Text-mode tutor responses arrive through LiveKit chat rather than audio transcription.
  // Mirror the newest agent-authored message into the same transcript panel used by voice mode.
  useEffect(() => {
    if (!oneToOneMeetingStatus || !Array.isArray(chatMessages)) return;
    const latest = latestAgentChatMessage(chatMessages);
    if (!latest) return;
    const identity = latest.id;
    if (writesReplies(tutorMode) && !openingAckSentRef.current) {
      openingAckSentRef.current = true;
      Promise.resolve()
        .then(() => sendChatMessage(`${TUTOR_OPENING_RECEIVED_PREFIX}:${identity}`))
        .catch(() => {
          openingAckSentRef.current = false;
        });
    }
    if (renderedTutorChatRef.current === identity) return;
    renderedTutorChatRef.current = identity;
    setTranscriptSourceKey(`tutor:chat_${identity}`);
    setTranscriptSourceText(latest.text);
    clearTimeout(releaseTimeout.current);
    setTurn('prompt');
  }, [chatMessages, oneToOneMeetingStatus, tutorMode, sendChatMessage]);

  // The guided first visit moves on once the tutor has finished answering something the learner asked (Sol
  // player-tour Q2 A) - not the opening greeting: an answer is the tutor speaking or writing after a question.
  const awaitingAnswerRef = useRef(false);
  const previousTurnRef = useRef(turn);
  useEffect(() => {
    const previous = previousTurnRef.current;
    previousTurnRef.current = turn;
    // a question went to the tutor (round 13.14: after a finished lesson, a question and the tutor off -> Leave unit)
    if (turn === 'thinking' && previous !== 'thinking') emitFirstVisit('tutor-asked');
    if (turn === 'thinking') awaitingAnswerRef.current = true;
    if (turn === 'connecting') awaitingAnswerRef.current = false;
    if (turn === 'prompt' && awaitingAnswerRef.current && (previous === 'speaking' || previous === 'thinking')) {
      awaitingAnswerRef.current = false;
      emitFirstVisit('tutor-answered');
    }
  }, [turn]);

  // The tutor is "connected" once its participant has joined the room.
  const connected = !!agentParticipant;

  // Acknowledge that the browser has installed its chat observers before the worker publishes the
  // opening. The worker ignores this reserved protocol message and retains a legacy timeout for
  // older clients.
  useEffect(() => {
    if (
      !oneToOneMeetingStatus ||
      !writesReplies(tutorMode) ||
      !connected ||
      clientReadySentRef.current
    ) return;
    let cancelled = false;
    let retryTimer;
    let attempts = 0;
    clientReadySentRef.current = true;
    const acknowledgeReady = () => {
      attempts += 1;
      Promise.resolve()
        .then(() => sendChatMessage(TUTOR_CLIENT_READY_MESSAGE))
        .catch(() => {
          if (cancelled) return;
          clientReadySentRef.current = false;
          if (attempts >= 3) return;
          retryTimer = setTimeout(() => {
            if (cancelled || clientReadySentRef.current) return;
            clientReadySentRef.current = true;
            acknowledgeReady();
          }, 300);
        });
    };
    acknowledgeReady();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [oneToOneMeetingStatus, tutorMode, connected, sendChatMessage]);

  // Connection drives connecting → prompt. When the tutor's participant is gone
  // (not connected) we show 'connecting'; once it joins we hand the floor to you
  // ('prompt') until the tutor actually starts speaking — the transcript handler
  // below flips it to 'speaking'. 'thinking' and 'speaking' are set from real
  // events (your submission / the tutor's transcript), never from VAD.
  useEffect(() => {
    if (!connected) {
      clearTimeout(releaseTimeout.current);
      setTurn('connecting');
      return;
    }
    setTurn(prev => (prev === 'connecting' ? 'prompt' : prev));
  }, [connected]);

  // — your transcription + data‐channel logic unchanged —
  const aat = tracks.find(
    t => t.publication.kind === Track.Kind.Audio && t.participant.isAgent,
  );
  let agentAudioTrack;
  if (aat) agentAudioTrack = aat;
  else if (agentParticipant) {
    agentAudioTrack = {
      participant: agentParticipant,
      source: Track.Source.Microphone,
    };
  }

  const chatTileContent = useMemo(() => {
    if (!agentAudioTrack) return null;
    return (
      <TranscriptionTile
        agentAudioTrack={agentAudioTrack}
        accentColor="#FF0000"
        onTranscript={({id, text, final, participant}) => {
          setTranscriptSourceKey(`${participant}:${id}`);
          setTranscriptSourceText(text || '');
          if (participant === 'player') {
            // Your spoken question: hand the turn to the tutor on a final,
            // non-empty line (the moment it's sent as livekit_final_response …
            // player). Interim/empty lines don't count, so noise can't flip it.
            if (final && (text || '').trim()) {
              setTurn('thinking');
            }
            return;
          }
          if (participant === 'tutor') {
            // Tutor transcript is streaming → the tutor is actively speaking.
            // This is the reliable "tutor speaking" signal on web. Re-arm the
            // debounce on every segment; when the stream goes quiet for
            // AGENT_RELEASE_MS, hand the turn back to you ('prompt').
            setTurn('speaking');
            clearTimeout(releaseTimeout.current);
            releaseTimeout.current = setTimeout(() => {
              setTurn(prev => (prev === 'speaking' ? 'prompt' : prev));
            }, AGENT_RELEASE_MS);
          }
        }}
      />
    );
  }, [agentAudioTrack]);

  // Live session meter and a clear end (owner items 8 and 11; Sol owner14-e). The worker extends the
  // session before each deadline and says when the learner's minutes run out, so the room never goes
  // quiet on "Connecting...".
  const dispatch = useDispatch();
  const [secondsLeft, setSecondsLeft] = useState(null);
  const endedRef = useRef(false);
  const everConnectedRef = useRef(false);
  const endReasonRef = useRef(null);
  const endSession = useCallback(
    ended => {
      if (endedRef.current) return;
      endedRef.current = true;
      endReasonRef.current = ended?.reason || 'session_ended';
      dispatch(showQuotaPaywall(sessionEndNotice(ended)));
      dispatch(stopOneOnOneMeeting());
    },
    [dispatch],
  );
  // A quiet minute (the learner's turn, nothing typed or said, the tutor silent) asks "Still there?"; no answer in 15 s
  // ends the session so it stops using the learner's minutes (Sol tutor-idle C, same as the iPhone app; the tutor worker
  // also ends a session after 90 s idle). Any speech, typing or reply restarts the minute; "Keep Talking" tells the worker.
  const [activityAt, setActivityAt] = useState(0);
  const [stillThere, setStillThere] = useState(false);
  const noteActivity = useCallback(() => setActivityAt(Date.now()), []);
  useEffect(() => {
    noteActivity();
  }, [turn, transcriptSourceText, noteActivity]);
  useEffect(() => {
    setStillThere(false);
    if (!oneToOneMeetingStatus || turn !== 'prompt') return undefined;
    const warn = setTimeout(() => setStillThere(true), TUTOR_IDLE_WARN_MS);
    return () => clearTimeout(warn);
  }, [oneToOneMeetingStatus, turn, activityAt]);
  useEffect(() => {
    if (!stillThere) return undefined;
    const close = setTimeout(
      () => endSession({type: 'ended', reason: 'IDLE', meter: writesReplies(tutorMode) ? 'text' : 'voice'}),
      TUTOR_IDLE_CLOSE_MS,
    );
    return () => clearTimeout(close);
  }, [stillThere, endSession, tutorMode]);
  const keepTalking = useCallback(() => {
    setStillThere(false);
    noteActivity();
    Promise.resolve(sendChatMessage(TUTOR_STILL_HERE_MESSAGE)).catch(() => {});
  }, [noteActivity, sendChatMessage]);

  useDataChannel(METER_TOPIC, msg => {
    const data = parseMeterMessage(msg?.payload);
    if (data?.type === 'meter') setSecondsLeft(data.secondsLeft);
    if (data?.type === 'ended') endSession(data);
  });
  // Tutor events (Sol mixpanel-coverage D1 A): session start/end, questions and finished answers, minutes used
  useTutorAnalytics({ active: oneToOneMeetingStatus, connected, mode: tutorMode, turn, secondsLeft, endReasonRef });
  useEffect(() => {
    endedRef.current = false;
    everConnectedRef.current = false;
    setSecondsLeft(null);
  }, [oneToOneMeetingStatus]);
  // A tutor that leaves without an "ended" message (older worker, crash, network) must not leave the
  // learner on "Connecting..." forever: after a short grace period, end the session and say so.
  useEffect(() => {
    if (!oneToOneMeetingStatus) return undefined;
    if (connected) {
      everConnectedRef.current = true;
      return undefined;
    }
    const graceMs = everConnectedRef.current ? 8000 : 45000;
    const timer = setTimeout(() => endSession({reason: 'SESSION_ENDED'}), graceMs);
    return () => clearTimeout(timer);
  }, [connected, oneToOneMeetingStatus, endSession]);

  // Running low (two minutes): Upgrade opens the plans; the top plan buys more instead.
  const planInfo = useSelector(state => state.subscriptionUi?.plan || null);
  // The free tour question uses none of the plan: no "running low" offer while it runs.
  const lowAction = freeTourQuestion ? null : lowMinutesAction(secondsLeft, planInfo);
  const lowMinutes = lowAction === 'buyMore'
    ? {label: 'Buy more', onPress: () => dispatch(setBuyMore(true))}
    : lowAction === 'upgrade'
      ? {label: 'Upgrade', onPress: () => dispatch(openPlans())}
      : null;

  // Voice by default with "Type instead" / "Talk instead" on the bar (Sol onboarding-build Q1 A). Hidden only while the
  // server-granted free tour question runs: switching would end its one free session, and typing works on the bar in
  // either mode (Sol onboarding-build-2 D1 A).
  // Three questions to tap on every turn, every session (Sol onboarding-leftovers S1 B / S2 A); the tour's free question
  // offers its course's own questions there too, as the owner's prototype does.
  // The example questions show only when tutor mode opens; once the learner asks anything they are gone for the rest of
  // the session (owner 15 Oct: "those questions should only be opened for the first time when they enter into tutor mode
  // and not always" - over Sol tutor-chips A). A new tutor session shows them again. Same as the iPhone app.
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (turn === 'thinking') setAsked(true);
  }, [turn]);
  useEffect(() => {
    setAsked(false);
  }, [oneToOneMeetingStatus]);

  const suggestions = SIMPLIFIED_FIRST_VISIT
    ? freeTourQuestion ? tourSuggestions() : TUTOR_SUGGESTIONS
    : null;
  // The microphone is on in a voice session: the learner just speaks (owner 11 Oct, voice-first).
  const voiceLive = Boolean(listensToLearner(tutorMode) && isMicrophoneEnabled);
  const modeSwitch = SIMPLIFIED_FIRST_VISIT && !freeTourQuestion
    ? tutorMode === 'VOICE'
      ? {label: 'Type Instead', icon: 'keyboard-outline', onPress: () => dispatch(requestTutorSwitch(TEXT_MODE))}
      : {label: 'Talk Instead', icon: 'microphone-outline', onPress: () => dispatch(requestTutorSwitch('VOICE'))}
    : null;

  return (
    <>
      {chatTileContent}
      {oneToOneMeetingStatus && (
        <LiveKitTranscriptPanel
          sourceKey={transcriptSourceKey}
          sourceText={transcriptSourceText}
          wholeTutorReplies={writesReplies(tutorMode)}
        />
      )}
      {oneToOneMeetingStatus && stillThere ? <TutorIdleCard onKeepTalking={keepTalking} /> : null}
      {oneToOneMeetingStatus &&
      <LiveKitTextInput
        onActivity={noteActivity}
        status={turn}
        timeLeftLabel={freeTourLabel(minutesLeftLabel(freeTourQuestion ? planSecondsLeft : secondsLeft), freeTourQuestion)}
        lowMinutesAction={lowMinutes}
        // Typing works in every mode, so a missing or refused microphone is never a dead end (Sol onboarding-build Q1).
        micOptional={writesReplies(tutorMode) || SIMPLIFIED_FIRST_VISIT}
        // "Voice Mode" / "Chat Mode" on the bar for the whole session (Sol ui-leftovers G9).
        modeLabel={tutorModeLabel(tutorMode)}
        modeSwitch={modeSwitch}
        suggestions={asked ? null : suggestions}
        voiceLive={voiceLive}
        showMicPermissionWarning={showMicPermissionWarning}
        onMessageSent={(msg) => {
          // A typed answer also hands the turn to the tutor.
          setTurn('thinking');
          // Reflect typed message in transcript UI (non-stacking).
          setTranscriptSourceKey(`player:typed_${Date.now()}`);
          setTranscriptSourceText(msg || '');
        }}
      />}
    </>
  );
}
